// import { DndContext, DragOverEvent } from '@dnd-kit/core';
import classNames from 'classnames';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WBS_ELEMENT_BASE_URL } from '../../../../api/api';
import { useGenericAPI } from '../../../../api/useGenericApi';
import { ProCol } from '../../../../atoms/Grid/Col';
import { ProRow } from '../../../../atoms/Grid/Row';
import { ProValSwitch } from '../../../../atoms/TwoValSwitch/TwoValSwitch';
import { ApplicationElementType, getBaseBCItemForType } from '../../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../../components/Page/Page';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { UserContext } from '../../../../contexts/UserContext';
import { Components } from '../../../../utils/types';
import {
    RequirementDetailWindowType,
    useRequirementDetailsFrame,
} from '../../Requirements/modules/RequirementDetailsFrame';
import { StoryDroppable } from './components/StoryDroppable';
import { StroryDraggable } from './components/StroryDraggable';
import { DndContext, DragOverEvent } from '@dnd-kit/core';

export const DaiyCeremonyPageStoryBoard = ({
    selectedSprint,
    toggleView,
}: {
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    toggleView: () => void;
}) => {
    const { t } = useTranslation();
    const { selectedTeam } = useContext(ProjectContext);
    const { portfolio } = useContext(UserContext);
    const reqStates = useMemo(() => portfolio?.requirementStates, [portfolio]);

    const {
        data: sprint,
        error,
        loading,
        sync,
    } = useGenericAPI<Components.Schemas.WBSElementDTO>(WBS_ELEMENT_BASE_URL, selectedSprint?.id);

    const openTickets = useMemo(
        () => sprint?.requirements?.filter((cur) => cur.requirementState?.requirementStateType === 'Scheduled'),
        [sprint]
    );
    const doingTickets = useMemo(
        () => sprint?.requirements?.filter((cur) => cur.requirementState?.requirementStateType === 'UnderDevelopment'),
        [sprint]
    );
    const reviewTickets = useMemo(
        () => sprint?.requirements?.filter((cur) => cur.requirementState?.requirementStateType === 'ReadyForReview'),
        [sprint]
    );
    const holdTickets = useMemo(
        () => sprint?.requirements?.filter((cur) => cur.requirementState?.requirementStateType === 'OnHold'),
        [sprint]
    );
    const doneTickets = useMemo(
        () => sprint?.requirements?.filter((cur) => cur.requirementState?.requirementStateType === 'Done'),
        [sprint]
    );

    const [selectedRequirement, setSelectedRequirement] = useState<Components.Schemas.RequirementDTO>();

    const deselectTableRow = () => {
        setSelectedRequirement(undefined);
        open('hidden');
    };

    const selectElement = (id?: string) => {
        const req = sprint?.requirements?.find((cur) => cur.id === id);
        if (req) {
            open('view');
            setSelectedRequirement(req);
        }
    };

    const { open, renderDetailWindow, saveAction, isDisplayed } = useRequirementDetailsFrame({
        closeAction: deselectTableRow,
        id: selectedRequirement?.id,
        selectAction: selectElement,
        externalSync: sync,
        type: RequirementDetailWindowType.Daily,
    });

    function handleDragEnd({ over, active }: DragOverEvent) {
        const state = reqStates?.find((cur) => cur.requirementStateType === over?.id);
        const req = sprint?.requirements?.find((cur) => cur.id === active.id);

        //Todo: the req has no wbs element for now and the update deletes it
        if (req?.requirementState?.requirementStateType !== over?.id)
            saveAction({ ...req, requirementState: state, wbsElement: selectedSprint });
    }

    return (
        <DndContext onDragEnd={handleDragEnd}>
            <ProPage
                breadCrump={getBaseBCItemForType(
                    [
                        ApplicationElementType.TeamManagement,
                        { type: ApplicationElementType.TeamManagement, name: selectedTeam?.name },
                    ],
                    t
                )}
                name={'Daily standup / Processing: Stories'}
                loading={loading}
                error={error}
                filter={<ProValSwitch left="Story board" right="Task board" toggle={toggleView} aktive="left" />}
            >
                <ProRow className="Daily">
                    <ProCol span={3} className="Daily__story-row">
                        <h3>Open / scheduled ({openTickets?.length})</h3>
                        <ul>
                            {openTickets?.map((cur) => (
                                <StroryDraggable
                                    id={cur.id ?? ''}
                                    key={`ticket-${cur.name}`}
                                    req={cur}
                                    selectElement={selectElement}
                                />
                            ))}
                            <StoryDroppable id="Scheduled" />
                        </ul>
                    </ProCol>
                    <ProCol span={3} className="Daily__story-row">
                        <h3>On hold ({holdTickets?.length})</h3>
                        <ul>
                            {holdTickets?.map((cur) => (
                                <StroryDraggable
                                    id={cur.id ?? ''}
                                    key={`ticket-${cur.name}`}
                                    req={cur}
                                    selectElement={selectElement}
                                />
                            ))}
                            <StoryDroppable id="OnHold" />
                        </ul>
                    </ProCol>
                    <ProCol span={3} className="Daily__story-row">
                        <h3>Doing / in development ({doingTickets?.length})</h3>
                        <ul>
                            {doingTickets?.map((cur) => (
                                <StroryDraggable
                                    id={cur.id ?? ''}
                                    key={`ticket-${cur.name}`}
                                    req={cur}
                                    selectElement={selectElement}
                                />
                            ))}
                            <StoryDroppable id="UnderDevelopment" />
                        </ul>
                    </ProCol>
                    <ProCol span={3} className="Daily__story-row">
                        <h3>Review / ready for review ({reviewTickets?.length})</h3>
                        <ul>
                            {reviewTickets?.map((cur) => (
                                <StroryDraggable
                                    id={cur.id ?? ''}
                                    key={`ticket-${cur.name}`}
                                    req={cur}
                                    selectElement={selectElement}
                                />
                            ))}
                            <StoryDroppable id="ReadyForReview" />
                        </ul>
                    </ProCol>
                    <ProCol span={3} className="Daily__story-row">
                        <h3>Done ({doneTickets?.length})</h3>
                        <ul>
                            {doneTickets?.map((cur) => (
                                <StroryDraggable
                                    id={cur.id ?? ''}
                                    key={`ticket-${cur.name}`}
                                    req={cur}
                                    selectElement={selectElement}
                                />
                            ))}
                            <StoryDroppable id="Done" />
                        </ul>
                    </ProCol>
                </ProRow>
                <div
                    className={classNames(
                        'ProApp--detailWindowSection',
                        isDisplayed && 'ProApp--detailWindowSection__expanded'
                    )}
                >
                    {renderDetailWindow()}
                </div>
            </ProPage>
        </DndContext>
    );
};
